import * as S from './WBMenu.styles';

import React from 'react';
import * as _ from 'lodash';
import computeScrollIntoView from 'compute-scroll-into-view';

export interface WBMenuOption {
  name?: string;
  value: string | number;
  icon?: string;
  disabled?: boolean;
  render?(props: {hovered: boolean; selected: boolean}): React.ReactNode;
  onSelect?(): void;
}

export function getOptionDisplayName(option: WBMenuOption) {
  return option.name ?? option.value;
}

const scrollIntoView: typeof computeScrollIntoView = (target, options) => {
  const actions = computeScrollIntoView(target, options);
  actions.forEach(({el, top, left}) => {
    if (el !== document.documentElement) {
      el.scrollTop = top;
      el.scrollLeft = left;
    }
  });
  return actions;
};

const DEFAULT_OPTION_RENDERER: OptionRenderer = ({
  option,
  hovered,
  selected,
}) => (
  <S.Item hovered={hovered}>
    <S.ItemIcon name={option.icon ?? (selected ? 'check' : 'blank')} />
    {getOptionDisplayName(option)}
  </S.Item>
);

export type OptionRenderer = (props: {
  option: WBMenuOption;
  hovered: boolean;
  selected: boolean;
}) => React.ReactNode;

export type WBMenuOnSelectHandler = (
  value: string | number,
  extra: {option: WBMenuOption}
) => void;

export interface WBMenuProps {
  className?: string;
  options: WBMenuOption[];
  optionRenderer?: OptionRenderer;
  // by default expands to fit longest item
  width?: number;
  selected?: string | number;
  selectedRef?: React.Ref<HTMLDivElement>;
  backgroundColor?: string;
  highlightFirst?: boolean;
  onSelect?: WBMenuOnSelectHandler;
  onEsc?(): void;
}

const WBMenu = React.forwardRef<HTMLDivElement, WBMenuProps>(
  (
    {
      className,
      options,
      optionRenderer = DEFAULT_OPTION_RENDERER,
      width,
      selected,
      selectedRef,
      backgroundColor,
      highlightFirst,
      onSelect,
      onEsc,
    },
    ref
  ) => {
    const getDefaultHoverIndex = React.useCallback(() => {
      if (highlightFirst && options.length > 0) {
        return _.findIndex(options, opt => !opt.disabled);
      }
      return _.findIndex(options, opt => selected === opt.value);
    }, [highlightFirst, options, selected]);
    // dedupe options
    const prevLength = options.length;
    options = _.uniqBy(options, opt => opt.value);
    if (options.length !== prevLength) {
      console.warn('Passed duplicate options into WBMenu');
    }

    const [hoveredIndex, setHoveredIndex] = React.useState(
      getDefaultHoverIndex()
    );
    const lastHoveredValue = React.useRef(options[hoveredIndex]?.value);
    const setHoveredIndexWrapped = React.useCallback(
      (i: number) => {
        setHoveredIndex(i);
        lastHoveredValue.current = options[i]?.value;
      },
      [options]
    );

    React.useEffect(() => {
      if (
        lastHoveredValue.current == null ||
        options[hoveredIndex]?.value !== lastHoveredValue.current
      ) {
        setHoveredIndexWrapped(getDefaultHoverIndex());
      }
    }, [getDefaultHoverIndex, setHoveredIndexWrapped, hoveredIndex, options]);
    const contentRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
      function onKeyDown(e: KeyboardEvent) {
        if (e.keyCode === 38 /* up */) {
          e.preventDefault();
          let moved = false;
          for (let i = hoveredIndex - 1; i >= -1; i--) {
            const modIndex =
              ((i % options.length) + options.length) % options.length;
            if (!options[modIndex].disabled) {
              setHoveredIndexWrapped(modIndex);
              const child = contentRef.current?.children[modIndex];
              if (child) {
                scrollIntoView(child, {
                  scrollMode: 'if-needed',
                  block: 'start',
                });
              }
              moved = true;
              break;
            }
          }
          if (!moved && contentRef.current != null) {
            scrollIntoView(contentRef.current, {
              scrollMode: 'if-needed',
              block: 'start',
            });
          }
        }
        if (e.keyCode === 40 /* down */) {
          e.preventDefault();
          let moved = false;
          for (let i = hoveredIndex + 1; i <= options.length; i++) {
            const modIndex =
              ((i % options.length) + options.length) % options.length;
            if (!options[modIndex].disabled) {
              setHoveredIndexWrapped(modIndex);
              const child = contentRef.current?.children[modIndex];
              if (child) {
                scrollIntoView(child, {
                  scrollMode: 'if-needed',
                  block: 'end',
                });
              }
              moved = true;
              break;
            }
          }
          if (!moved && contentRef.current != null) {
            scrollIntoView(contentRef.current, {
              scrollMode: 'if-needed',
              block: 'end',
            });
          }
        }
        if (e.keyCode === 13 && !e.shiftKey /* enter */) {
          e.preventDefault();
          if (options[hoveredIndex]) {
            onSelect?.(options[hoveredIndex].value, {
              option: options[hoveredIndex],
            });
          }
        }
        if (e.keyCode === 27 /* esc */) {
          e.preventDefault();
          onEsc?.();
        }
      }
      document.addEventListener('keydown', onKeyDown);
      return () => {
        document.removeEventListener('keydown', onKeyDown);
      };
    }, [hoveredIndex, onEsc, onSelect, options, setHoveredIndexWrapped]);

    const contentCallbackRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        if (ref) {
          if (typeof ref === 'function') {
            ref(node);
          } else {
            (ref as any).current = node;
          }
        }
        (contentRef as any).current = node;
      },
      [ref]
    );

    return (
      <S.Content
        ref={contentCallbackRef}
        className={className}
        width={width}
        backgroundColor={backgroundColor}>
        {options.map((option, i) => {
          const isSelected = selected === option.value;
          const isHovered = hoveredIndex === i;
          return (
            <div
              key={option.value}
              ref={isSelected ? selectedRef : undefined}
              onMouseEnter={() => {
                setHoveredIndexWrapped(i);
              }}
              style={option.disabled ? {pointerEvents: 'none'} : undefined}
              onMouseDown={e => {
                e.preventDefault();
              }}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                option.onSelect?.();
                onSelect?.(option.value, {option});
              }}>
              {option.render
                ? option.render({hovered: isHovered, selected: isSelected})
                : optionRenderer({
                    option,
                    hovered: isHovered,
                    selected: isSelected,
                  })}
            </div>
          );
        })}
      </S.Content>
    );
  }
);

export default WBMenu;
